// Cliente do Simples Ledger (docs/ledger-v0.md) — sem RPC, sem topics, sem
// eth_getLogs: o nó já devolve os campos prontos ({from,to,value,ref,ts,hash,
// idx,type}). Backfill paginado via /txs + tempo real via SSE /stream, com
// fallback pra polling se o SSE cair.

const BACKFILL_PAGE = 500;
const POLL_INTERVAL_MS = 5000;
const SSE_RETRY_MS = 3000;

function normalizeTx(raw) {
  return {
    ...raw,
    value: BigInt(raw.value),
    timestamp: raw.ts,
  };
}

/**
 * Backfill inicial: pagina /txs?since=<idx>&limit=500 até esgotar o log.
 * Chama onPage(txs[]) a cada página não-vazia. Retorna o maior idx visto
 * (ou -1 se o log estava vazio).
 */
export async function backfill(apiUrl, onPage) {
  let since = -1;
  for (;;) {
    const res = await fetch(`${apiUrl}/txs?since=${since}&limit=${BACKFILL_PAGE}`);
    if (!res.ok) throw new Error(`ledger HTTP ${res.status} em /txs`);
    const page = await res.json();
    if (!page.length) break;
    const txs = page.map(normalizeTx);
    onPage(txs);
    since = txs[txs.length - 1].idx;
    if (page.length < BACKFILL_PAGE) break;
  }
  return since;
}

/**
 * Assina o fluxo em tempo real: tenta SSE (/stream) com reconexão; se o SSE
 * falhar, cai pra polling (/txs?since=<último idx> a cada 5s) até o SSE
 * voltar a funcionar. onStatus(status) informa 'live' | 'reconnecting' |
 * 'polling' pra quem quiser refletir isso na UI (opcional).
 */
export function subscribe(apiUrl, { onTx, onStatus, lastIdx = -1 } = {}) {
  let closed = false;
  let cursor = lastIdx;
  let es = null;
  let pollTimer = null;
  let sseRetryTimer = null;

  function stopPolling() {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  function startPolling() {
    if (pollTimer || closed) return;
    onStatus?.('polling');
    const poll = async () => {
      if (closed) return;
      try {
        const res = await fetch(`${apiUrl}/txs?since=${cursor}&limit=${BACKFILL_PAGE}`);
        if (res.ok) {
          const page = await res.json();
          for (const raw of page) {
            const tx = normalizeTx(raw);
            cursor = tx.idx;
            onTx(tx);
          }
        }
      } catch (err) {
        console.error('[ledger] falha no poll incremental', err);
      } finally {
        if (!closed) pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
  }

  function startSSE() {
    if (closed) return;
    onStatus?.('reconnecting');
    try {
      es = new EventSource(`${apiUrl}/stream`);
    } catch (err) {
      console.error('[ledger] falha ao abrir SSE', err);
      startPolling();
      scheduleSseRetry();
      return;
    }

    es.onopen = () => {
      onStatus?.('live');
      stopPolling();
    };

    es.onmessage = (event) => {
      try {
        const raw = JSON.parse(event.data);
        const tx = normalizeTx(raw);
        cursor = tx.idx;
        onTx(tx);
      } catch (err) {
        console.error('[ledger] payload de SSE inválido', err, event.data);
      }
    };

    es.onerror = () => {
      es?.close();
      es = null;
      if (closed) return;
      // sem stream vivo: garante que os dados continuam chegando via polling
      // enquanto tentamos reconectar o SSE em segundo plano.
      startPolling();
      scheduleSseRetry();
    };
  }

  function scheduleSseRetry() {
    if (sseRetryTimer || closed) return;
    sseRetryTimer = setTimeout(() => {
      sseRetryTimer = null;
      startSSE();
    }, SSE_RETRY_MS);
  }

  if (typeof EventSource === 'function') {
    startSSE();
  } else {
    startPolling();
  }

  return {
    close() {
      closed = true;
      stopPolling();
      if (sseRetryTimer) clearTimeout(sseRetryTimer);
      if (es) es.close();
    },
  };
}
